import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IMAGE_ICON_PATH,
  TELEGRAM_ICON_PATH,
} from 'constants/images.constants';
import { PaymentStatusView } from 'enums';
import { useOrderContext, useTrackFlowEvent } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PaymentStatusViewProps } from 'types';
import { DefaultUploadReceiptDialog } from 'views/DefaultOrderView/components/DefaultUploadReceiptDialog';

import { SBER_QR_PALETTE } from '../../constants/sber-qr-palette.constant';

const SuccessIcon: React.FC = () => (
  <div
    style={{
      background: SBER_QR_PALETTE.brightBg,
      boxShadow: `0 0 36px rgba(37,209,139,.2)`,
    }}
    className="tw-w-[80px] tw-h-[80px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6"
  >
    <div className="tw-w-[32px] tw-h-[17px] tw-border-b-[4px] tw-border-l-[4px] tw-border-solid tw-border-[#37eba0] tw-rotate-[-45deg] tw-translate-y-[-4px]" />
  </div>
);

const ErrorIcon: React.FC = () => (
  <div
    style={{
      background: 'rgba(248,113,113,.1)',
      boxShadow: '0 0 36px rgba(248,113,113,.12)',
    }}
    className="tw-w-[80px] tw-h-[80px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6"
  >
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
      <path
        d="M8 8L22 22M22 8L8 22"
        stroke="#f87171"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

export const SberQrPaymentStatus: React.FC<PaymentStatusViewProps> = ({
  actions,
  canManageReceipts,
}) => {
  const {
    paymentStatusView,
    refetchOrder,
    receiptsResult: {
      receipts,
      uppy,
      setReceiptUploadCallbacks,
      telegramSupportBotUrl,
      handleTelegramOrderHelp,
      handleTelegramUploadReceipt,
    },
  } = useOrderContext();

  const isSuccess = paymentStatusView === PaymentStatusView.Success;

  useTrackFlowEvent();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: `components.payment_${paymentStatusView}`,
  });
  const { t: tReceipts } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_receipts_dispute',
  });

  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const action = useMemo(() => actions[actions.length - 1], [actions]);

  const hasReceipts = !isEmpty(receipts);

  return (
    <div>
      <section
        style={{ borderColor: SBER_QR_PALETTE.line }}
        className="tw-max-w-lg tw-mx-auto tw-border tw-border-solid tw-rounded-[32px] tw-px-[34px] tw-pt-[40px] tw-pb-[34px] tw-bg-[linear-gradient(145deg,rgba(22,43,61,.98),rgba(10,26,40,.99))] tw-shadow-[0_24px_80px_rgba(0,0,0,.42)] tw-text-center"
      >
        {isSuccess ? <SuccessIcon /> : <ErrorIcon />}

        <strong
          style={{ color: SBER_QR_PALETTE.text }}
          className="tw-block tw-text-[22px] tw-font-[700] tw-tracking-[-0.02em] tw-mb-2"
        >
          {t('title')}
        </strong>

        <p
          style={{ color: SBER_QR_PALETTE.muted }}
          className="tw-m-0 tw-mb-8 tw-text-[14px] tw-leading-[1.55]"
        >
          {t('message')}
        </p>

        {action && (
          <button
            onClick={action.onClick}
            style={
              isSuccess
                ? {
                    background: `linear-gradient(135deg, ${SBER_QR_PALETTE.bright}, #19bc78)`,
                    color: '#04170e',
                  }
                : {
                    borderColor: SBER_QR_PALETTE.line,
                    color: SBER_QR_PALETTE.muted,
                    background: 'transparent',
                  }
            }
            className="tw-w-full tw-min-h-[50px] tw-rounded-[17px] tw-text-[15px] tw-font-[700] tw-border tw-border-solid tw-cursor-pointer tw-shadow-none"
          >
            {action.label}
          </button>
        )}

        {canManageReceipts && (
          <div
            style={{ borderTopColor: SBER_QR_PALETTE.line }}
            className="tw-mt-6 tw-pt-6 tw-border-t tw-border-solid"
          >
            {hasReceipts ? (
              <>
                <p
                  style={{ color: SBER_QR_PALETTE.text }}
                  className="tw-m-0 tw-mb-4 tw-text-[14px] tw-font-[600]"
                >
                  {tReceipts('receipts_uploaded.title')}
                </p>
                {!!telegramSupportBotUrl && (
                  <>
                    <p
                      style={{ color: SBER_QR_PALETTE.muted }}
                      className="tw-m-0 tw-mb-4 tw-text-[13px] tw-leading-[1.5]"
                    >
                      {tReceipts('receipts_uploaded.description')}
                    </p>
                    <button
                      onClick={handleTelegramOrderHelp}
                      style={{
                        borderColor: SBER_QR_PALETTE.line,
                        color: SBER_QR_PALETTE.muted,
                      }}
                      className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-min-h-[46px] tw-rounded-[14px] tw-border tw-border-solid tw-bg-transparent tw-text-[14px] tw-cursor-pointer"
                    >
                      <img
                        src={TELEGRAM_ICON_PATH}
                        alt=""
                        className="tw-w-[18px] tw-h-[18px]"
                      />
                      {tReceipts('buttons.support')}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <p
                  style={{ color: SBER_QR_PALETTE.text }}
                  className="tw-m-0 tw-mb-1 tw-text-[14px] tw-font-[600]"
                >
                  {tReceipts('no_receipts.title')}
                </p>
                <p
                  style={{ color: SBER_QR_PALETTE.muted }}
                  className="tw-m-0 tw-mb-4 tw-text-[13px]"
                >
                  {tReceipts('no_receipts.description')}
                </p>
                <button
                  onClick={() => {
                    setReceiptUploadCallbacks(refetchOrder, () =>
                      setUploadModalOpen(false),
                    );
                    setUploadModalOpen(true);
                  }}
                  style={{
                    background: SBER_QR_PALETTE.brightBg,
                    borderColor: 'rgba(55,235,160,.25)',
                    color: SBER_QR_PALETTE.bright,
                  }}
                  className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-min-h-[46px] tw-rounded-[14px] tw-border tw-border-solid tw-text-[14px] tw-font-[600] tw-cursor-pointer tw-mb-3"
                >
                  <img
                    src={IMAGE_ICON_PATH}
                    alt=""
                    className="tw-w-[18px] tw-h-[18px]"
                  />
                  {tReceipts('buttons.upload')}
                </button>
                {!!telegramSupportBotUrl && (
                  <button
                    onClick={handleTelegramUploadReceipt}
                    style={{ color: SBER_QR_PALETTE.muted }}
                    className="tw-block tw-w-full tw-bg-transparent tw-border-0 tw-text-[13px] tw-underline tw-cursor-pointer"
                  >
                    {tReceipts('buttons.upload_by_telegram')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </section>

      <DefaultUploadReceiptDialog
        open={uploadModalOpen}
        uppy={uppy}
        onClose={() => setUploadModalOpen(false)}
      />
    </div>
  );
};
